import { Link } from "react-router-dom";
import { Users, FileText, ShieldCheck, AlertTriangle, Banknote, HeartHandshake, ClipboardList } from "lucide-react";
import { PageHero, SectionHeading, DisclaimerBar } from "../components/shared/Primitives";
import { useHandoverSession } from "../hooks/useHandoverSession";

const SECTIONS = [
  { icon: Users, title: "How independent care works", body: "What it is, what it isn't, and how it differs from an agency or regulated provider." },
  { icon: ClipboardList, title: "What families are responsible for", body: "A plain-English breakdown of the family's role in a private arrangement." },
  { icon: FileText, title: "Questions to ask a private carer", body: "An interview checklist that covers experience, DBS, insurance, references and expectations." },
  { icon: ShieldCheck, title: "Insurance, DBS and references", body: "What each one means, what to look for, and how to verify them properly." },
  { icon: AlertTriangle, title: "Red flags to avoid", body: "Warning signs in carers, arrangements and pricing — before they become problems." },
  { icon: Banknote, title: "Paying fairly and clearly", body: "What a fair rate looks like, how to structure it, and why transparency protects everyone." },
  { icon: HeartHandshake, title: "Why written agreements matter", body: "A short, shared document prevents almost every misunderstanding." },
];

export default function ForFamilies() {
  const session = useHandoverSession();
  const hasFamilyFree = session.ready && session.active && session.entitlements.includes("family_free");

  return (
    <div data-testid="page-for-families">
      <PageHero
        eyebrow="For Families"
        title="Arrange private care — with clarity and confidence."
        description="Independent care can work brilliantly. You just need structure from day one. JCC gives you that structure."
        accent="teal"
        bgTint="family"
      >
        {hasFamilyFree ? (
          <Link
            to="/workspace/family-starter"
            className="btn-teal"
            data-testid="families-cta-access"
          >
            Go to Family Starter
          </Link>
        ) : (
          <a
            href="/join/family-starter"
            className="btn-teal"
            data-testid="families-cta-access"
          >
            Join Family Starter — free
          </a>
        )}
        <Link to="/family-access" className="btn-outline" data-testid="families-cta-family-circle">Explore Family Care Office — £19/mo</Link>
      </PageHero>

      <section className="section pt-0">
        <div className="container-jcc">
          <Link to="/compare-memberships#family-memberships" className="text-xs text-ink-soft hover:text-teal underline">
            Compare all family memberships
          </Link>
        </div>
      </section>

      <section className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="What we cover" accent="teal"
            title="What every family should know before hiring."
            description="Seven essentials — from interviewing a carer to getting paid for care-related expenses right." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTIONS.map((s) => (
              <div key={s.title} className="card-base p-7 hover:-translate-y-1 transition-transform duration-300" data-testid={`family-pillar-${s.title.toLowerCase().split(' ').slice(0,2).join('-')}`}>
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-semibold text-xl">{s.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
            <div className="card-base p-7 bg-surface-family border-teal-100">
              <h3 className="font-display font-semibold text-xl text-teal">When an agency may be more appropriate</h3>
              <p className="mt-2 text-ink-muted text-sm leading-relaxed">Sometimes a regulated provider is the right answer — complex medical needs, 24/7 cover, or when a family simply doesn't have capacity to manage it directly. We'll help you recognise when.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface">
        <div className="container-jcc">
          <div className="max-w-3xl"><DisclaimerBar /></div>
        </div>
      </section>
    </div>
  );
}
